import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import './AdminAffiliatePanel.css';

function AdminAffiliatePanel() {
  const [activeSubTab, setActiveSubTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [couponCodes, setCouponCodes] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState({ minimum_payout: 100, cookie_duration_days: 30 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Detail view state
  const [selectedAffiliateDetails, setSelectedAffiliateDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    initial_percentage: 30,
    initial_days: 30,
    lifetime_percentage: 10,
    color: '#CD7F32'
  });

  const [couponForm, setCouponForm] = useState({
    code: '',
    affiliate_id: '',
    discount_percentage: 10,
    usage_limit_type: 'unlimited',
    max_uses: null,
    valid_until: null
  });

  useEffect(() => {
    loadData();
  }, [activeSubTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeSubTab === 'categories') {
        const data = await apiClient.getAffiliateCategories();
        setCategories(data);
      } else if (activeSubTab === 'affiliates') {
        const [affiliatesData, categoriesData] = await Promise.all([
          apiClient.getAllAffiliates(),
          apiClient.getAffiliateCategories()
        ]);
        setAffiliates(affiliatesData);
        setCategories(categoriesData);
      } else if (activeSubTab === 'coupons') {
        const [codesData, usersData] = await Promise.all([
          apiClient.getAllCouponCodes(),
          apiClient.getAllUsers()
        ]);
        setCouponCodes(codesData);
        setUsers(usersData.filter(u => u.is_affiliate)); // Filter nur Affiliates
      } else if (activeSubTab === 'settings') {
        const settingsData = await apiClient.getAffiliateSettings();
        setSettings(settingsData);
      } else if (activeSubTab === 'payouts') {
        const payoutsData = await apiClient.getAllPayouts();
        setPayouts(payoutsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const handleCreateCategory = async () => {
    setError('');
    
    try {
      if (editingCategory) {
        await apiClient.updateAffiliateCategory(editingCategory.id, categoryForm);
        setSuccess('Kategorie aktualisiert!');
      } else {
        await apiClient.createAffiliateCategory(categoryForm);
        setSuccess('Kategorie erstellt!');
      }
      
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', initial_percentage: 30, initial_days: 30, lifetime_percentage: 10, color: '#CD7F32' });
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern');
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      initial_percentage: cat.initial_percentage,
      initial_days: cat.initial_days,
      lifetime_percentage: cat.lifetime_percentage,
      color: cat.color
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    
    try {
      await apiClient.deleteAffiliateCategory(catId);
      loadData();
    } catch (err) {
      alert(err.message || 'Fehler beim Löschen');
    }
  };

  // Load affiliate details
  const handleViewAffiliateDetails = async (userId) => {
    try {
      setLoadingDetails(true);
      const details = await apiClient.getAffiliateDetails(userId);
      setSelectedAffiliateDetails(details);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Coupon handlers
  const handleCreateCoupon = async () => {
    setError('');
    
    if (!couponForm.code || !couponForm.affiliate_id) {
      setError('Code und Affiliate erforderlich');
      return;
    }

    try {
      await apiClient.createCouponCode(couponForm);
      setSuccess('Gutscheincode erstellt!');
      setShowCouponModal(false);
      setCouponForm({ code: '', affiliate_id: '', discount_percentage: 10, usage_limit_type: 'unlimited', max_uses: null, valid_until: null });
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen');
    }
  };

  const handleToggleCoupon = async (codeId) => {
    try {
      await apiClient.toggleCouponCode(codeId);
      loadData();
    } catch (err) {
      alert('Fehler beim Togglen');
    }
  };

  const handleDeleteCoupon = async (codeId) => {
    if (!window.confirm('Code wirklich löschen?')) return;
    
    try {
      await apiClient.deleteCouponCode(codeId);
      loadData();
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    try {
      await apiClient.updateAffiliateSettings(settings);
      setSuccess('Einstellungen gespeichert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern');
    }
  };

  // Affiliate management
  const handleAssignCategory = async (affiliateUserId, categoryId) => {
    try {
      await apiClient.assignAffiliateCategory(affiliateUserId, categoryId);
      setSuccess('Kategorie zugewiesen!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      alert(err.message || 'Fehler beim Zuweisen');
    }
  };

  const handleUpdatePayoutStatus = async (payoutId, status) => {
    try {
      await apiClient.updatePayoutStatus(payoutId, status, null);
      setSuccess(`Auszahlung ${status === 'paid' ? 'als bezahlt markiert' : status === 'approved' ? 'genehmigt' : 'abgelehnt'}!`);
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      alert(err.message || 'Fehler beim Aktualisieren');
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="affiliate-panel">
      {success && <div className="success-message">{success}</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Sub-Tabs */}
      <div className="affiliate-sub-tabs">
        <button
          className={`affiliate-sub-tab ${activeSubTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('categories')}
        >
          🏆 Kategorien
        </button>
        <button
          className={`affiliate-sub-tab ${activeSubTab === 'affiliates' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('affiliates')}
        >
          👥 Affiliate-Partner
        </button>
        <button
          className={`affiliate-sub-tab ${activeSubTab === 'coupons' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('coupons')}
        >
          🎫 Gutscheincodes
        </button>
        <button
          className={`affiliate-sub-tab ${activeSubTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('payouts')}
        >
          💳 Auszahlungen
        </button>
        <button
          className={`affiliate-sub-tab ${activeSubTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('settings')}
        >
          ⚙️ Einstellungen
        </button>
      </div>

      {/* Categories Tab */}
      {activeSubTab === 'categories' && (
        <div>
          <div className="admin-header">
            <h3 className="admin-title">Affiliate-Kategorien</h3>
            <button onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', initial_percentage: 30, initial_days: 30, lifetime_percentage: 10, color: '#CD7F32' }); setShowCategoryModal(true); }} className="btn-create">
              + Kategorie erstellen
            </button>
          </div>

          <div className="categories-grid">
            {categories.map((cat) => (
              <div key={cat.id} className="category-card" style={{ borderLeftColor: cat.color }}>
                <div className="category-header">
                  <h4 style={{ color: cat.color }}>{cat.name}</h4>
                  <div className="category-badge" style={{ background: cat.color + '20', color: cat.color, borderColor: cat.color + '40' }}>
                    {cat.initial_percentage}% → {cat.lifetime_percentage}%
                  </div>
                </div>
                <div className="category-details">
                  <p>📅 Erste {cat.initial_days} Tage: <strong>{cat.initial_percentage}%</strong></p>
                  <p>♾️ Danach (Lifetime): <strong>{cat.lifetime_percentage}%</strong></p>
                </div>
                <div className="category-actions">
                  <button onClick={() => handleEditCategory(cat)} className="btn-edit">Bearbeiten</button>
                  <button onClick={() => handleDeleteCategory(cat.id)} className="btn-delete">Löschen</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affiliates Tab */}
      {activeSubTab === 'affiliates' && (
        <div>
          <h3 className="admin-title">Affiliate-Partner verwalten</h3>
          
          {/* Detail View Modal */}
          {selectedAffiliateDetails && (
            <div className="affiliate-detail-modal">
              <div className="modal-content large">
                <div className="modal-header">
                  <h3>Affiliate-Partner Details</h3>
                  <button className="close-btn" onClick={() => setSelectedAffiliateDetails(null)}>×</button>
                </div>
                
                <div className="affiliate-detail-content">
                  {/* User Info */}
                  <div className="detail-section user-info">
                    <h4>👤 Partner-Info</h4>
                    <div className="info-grid">
                      <div><span className="label">Name:</span> {selectedAffiliateDetails.user?.name}</div>
                      <div><span className="label">E-Mail:</span> {selectedAffiliateDetails.user?.email}</div>
                      <div><span className="label">ZEN-ID:</span> <code>{selectedAffiliateDetails.user?.zen_id}</code></div>
                      <div><span className="label">Kategorie:</span> 
                        <span className="category-badge" style={{ 
                          background: selectedAffiliateDetails.category?.color + '20', 
                          color: selectedAffiliateDetails.category?.color 
                        }}>
                          {selectedAffiliateDetails.category?.name || 'Keine'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="detail-section stats-grid">
                    <div className="stat-box">
                      <div className="stat-value">{selectedAffiliateDetails.stats?.total_clicks || 0}</div>
                      <div className="stat-label">🖱️ Klicks</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-value">{selectedAffiliateDetails.stats?.total_referrals || 0}</div>
                      <div className="stat-label">👥 Registrierungen</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-value">{selectedAffiliateDetails.stats?.total_conversions || 0}</div>
                      <div className="stat-label">✅ Conversions</div>
                    </div>
                    <div className="stat-box highlight">
                      <div className="stat-value">{(selectedAffiliateDetails.stats?.total_earnings || 0).toFixed(2)}€</div>
                      <div className="stat-label">💰 Provisionen</div>
                    </div>
                  </div>

                  {/* Referrals with Earnings */}
                  <div className="detail-section">
                    <h4>👥 Geworbene User</h4>
                    {selectedAffiliateDetails.referrals?.length === 0 ? (
                      <p className="empty-text">Noch keine geworbenen User</p>
                    ) : (
                      <table className="detail-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>E-Mail</th>
                            <th>Registriert</th>
                            <th>Status</th>
                            <th>Verdient</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAffiliateDetails.referrals?.map((ref, idx) => (
                            <React.Fragment key={idx}>
                              <tr>
                                <td>{ref.user_name}</td>
                                <td>{ref.user_email}</td>
                                <td>{ref.registered_at ? new Date(ref.registered_at).toLocaleDateString('de-DE') : '-'}</td>
                                <td>
                                  <span className={`status-badge ${ref.is_converted ? 'converted' : ref.subscription_status}`}>
                                    {ref.is_converted ? '✅ Bezahlt' : ref.subscription_status === 'trial' ? '🆓 Trial' : ref.subscription_status}
                                  </span>
                                </td>
                                <td className="earned-col">{(ref.total_earned || 0).toFixed(2)}€</td>
                              </tr>
                              {/* Earnings breakdown */}
                              {ref.earnings && ref.earnings.length > 0 && (
                                <tr className="earnings-row">
                                  <td colSpan="5">
                                    <div className="earnings-breakdown-admin">
                                      {ref.earnings.map((e, eIdx) => (
                                        <div key={eIdx} className="earning-line">
                                          <span className="product-name">{e.product_name || 'Subscription'}</span>
                                          <span className="calc">
                                            {(e.product_price || 0).toFixed(2)}€ × {e.percentage_used}% = <strong>{(e.amount || 0).toFixed(2)}€</strong>
                                          </span>
                                          <span className={`type-badge ${e.earning_type}`}>
                                            {e.earning_type === 'initial' ? 'Initial' : 'Lifetime'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="affiliates-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Affiliate-Code</th>
                  <th>Kategorie</th>
                  <th>Klicks</th>
                  <th>Registrierungen</th>
                  <th>Conversions</th>
                  <th>Provisionen</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((aff) => (
                  <tr key={aff.user_id}>
                    <td>{aff.user_name}</td>
                    <td>{aff.user_email}</td>
                    <td className="code-text">{aff.affiliate_code || aff.zen_id}</td>
                    <td>
                      <select
                        value={aff.category_id || ''}
                        onChange={(e) => handleAssignCategory(aff.user_id, e.target.value)}
                        className="category-select"
                      >
                        <option value="">-- Kategorie --</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="stat-cell">🖱️ {aff.total_clicks || 0}</td>
                    <td className="stat-cell">👥 {aff.total_referrals || 0}</td>
                    <td className="stat-cell">
                      ✅ {aff.conversions || 0}
                      {(aff.total_referrals || 0) > 0 && (
                        <span className="conversion-rate"> ({aff.conversion_rate || 0}%)</span>
                      )}
                    </td>
                    <td className="stat-cell earnings">💰 {(aff.total_earnings || 0).toFixed(2)}€</td>
                    <td>
                      <button 
                        className="btn-details"
                        onClick={() => handleViewAffiliateDetails(aff.user_id)}
                        disabled={loadingDetails}
                      >
                        👁️ Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {affiliates.length === 0 && (
              <div className="empty-state">
                <p>Keine Affiliate-Partner registriert.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coupons Tab */}
      {activeSubTab === 'coupons' && (
        <div>
          <div className="admin-header">
            <h3 className="admin-title">Gutscheincodes</h3>
            <button onClick={() => { setCouponForm({ code: '', affiliate_id: '', discount_percentage: 10, usage_limit_type: 'unlimited', max_uses: null, valid_until: null }); setShowCouponModal(true); }} className="btn-create">
              + Code erstellen
            </button>
          </div>

          <div className="coupons-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Affiliate</th>
                  <th>Rabatt</th>
                  <th>Limit</th>
                  <th>Verwendet</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {couponCodes.map((code) => (
                  <tr key={code.id}>
                    <td className="code-text">{code.code}</td>
                    <td>{code.affiliate_name}</td>
                    <td>{code.discount_percentage}%</td>
                    <td>
                      {code.usage_limit.type === 'unlimited' && '∞ Unbegrenzt'}
                      {code.usage_limit.type === 'count' && `${code.usage_limit.max_uses}x`}
                      {code.usage_limit.type === 'date' && new Date(code.usage_limit.valid_until).toLocaleDateString('de-DE')}
                    </td>
                    <td>{code.times_used}x</td>
                    <td>
                      <span className={`status-badge ${code.is_active ? 'active' : 'inactive'}`}>
                        {code.is_active ? '✅ Aktiv' : '🔒 Gesperrt'}
                      </span>
                    </td>
                    <td className="table-actions">
                      <button onClick={() => handleToggleCoupon(code.id)} className="btn-toggle">
                        {code.is_active ? '🔒 Sperren' : '🔓 Aktivieren'}
                      </button>
                      <button onClick={() => handleDeleteCoupon(code.id)} className="btn-delete">Löschen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {couponCodes.length === 0 && (
              <div className="empty-state">
                <p>Noch keine Gutscheincodes erstellt.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeSubTab === 'settings' && (
        <div>
          <h3 className="admin-title">Affiliate-Einstellungen</h3>
          
          <div className="settings-form">
            <div className="setting-row">
              <label>💰 Mindest-Auszahlungsbetrag (EUR)</label>
              <input
                type="number"
                value={settings.minimum_payout}
                onChange={(e) => setSettings({ ...settings, minimum_payout: parseFloat(e.target.value) })}
                min="0"
                step="10"
              />
            </div>
            
            <div className="setting-row">
              <label>🍪 Referral-Cookie-Dauer (Tage)</label>
              <input
                type="number"
                value={settings.cookie_duration_days}
                onChange={(e) => setSettings({ ...settings, cookie_duration_days: parseInt(e.target.value) })}
                min="1"
                max="365"
              />
            </div>
            
            <button onClick={handleSaveSettings} className="btn-save">
              Einstellungen speichern
            </button>
          </div>
        </div>
      )}

      {/* Payouts Tab */}
      {activeSubTab === 'payouts' && (
        <div>
          <h3 className="admin-title">Auszahlungsanfragen verwalten</h3>
          
          <div className="payouts-admin-table">
            <table>
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Betrag</th>
                  <th>Methode</th>
                  <th>Zahlungsdetails</th>
                  <th>Beantragt</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>
                      <div className="affiliate-info">
                        <div className="affiliate-name">{payout.affiliate_name}</div>
                        <div className="affiliate-zen">{payout.affiliate_zen_id}</div>
                      </div>
                    </td>
                    <td className="amount-col">{payout.amount.toFixed(2)}€</td>
                    <td>{payout.payment_method === 'iban' ? '🏦 IBAN' : '💳 PayPal'}</td>
                    <td className="payment-details">
                      <div>{payout.payment_details}</div>
                      <div className="account-holder">{payout.account_holder_name}</div>
                    </td>
                    <td>{new Date(payout.requested_at).toLocaleDateString('de-DE')}</td>
                    <td>
                      <span className={`payout-status ${payout.status}`}>
                        {payout.status === 'requested' && '⏳ Beantragt'}
                        {payout.status === 'approved' && '✅ Genehmigt'}
                        {payout.status === 'paid' && '💰 Bezahlt'}
                        {payout.status === 'rejected' && '❌ Abgelehnt'}
                      </span>
                    </td>
                    <td className="table-actions">
                      {payout.status === 'requested' && (
                        <>
                          <button
                            onClick={() => handleUpdatePayoutStatus(payout.id, 'approved')}
                            className="btn-approve"
                          >
                            ✅ Genehmigen
                          </button>
                          <button
                            onClick={() => handleUpdatePayoutStatus(payout.id, 'rejected')}
                            className="btn-reject"
                          >
                            ❌ Ablehnen
                          </button>
                        </>
                      )}
                      {payout.status === 'approved' && (
                        <button
                          onClick={() => handleUpdatePayoutStatus(payout.id, 'paid')}
                          className="btn-mark-paid"
                        >
                          💰 Als bezahlt markieren
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {payouts.length === 0 && (
              <div className="empty-state">
                <p>Keine Auszahlungsanfragen vorhanden.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</h2>
              <button onClick={() => setShowCategoryModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="z.B. Platin"
                />
              </div>
              
              <div className="form-group">
                <label>Farbe</label>
                <input
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Provision erste X Tage (%)</label>
                  <input
                    type="number"
                    value={categoryForm.initial_percentage}
                    onChange={(e) => setCategoryForm({ ...categoryForm, initial_percentage: parseFloat(e.target.value) })}
                    min="0"
                    max="100"
                    step="5"
                  />
                </div>
                <div className="form-group">
                  <label>Anzahl Tage</label>
                  <input
                    type="number"
                    value={categoryForm.initial_days}
                    onChange={(e) => setCategoryForm({ ...categoryForm, initial_days: parseInt(e.target.value) })}
                    min="1"
                    max="365"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Lifetime-Provision (%)</label>
                <input
                  type="number"
                  value={categoryForm.lifetime_percentage}
                  onChange={(e) => setCategoryForm({ ...categoryForm, lifetime_percentage: parseFloat(e.target.value) })}
                  min="0"
                  max="100"
                  step="5"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCategoryModal(false)} className="btn-cancel">Abbrechen</button>
              <button onClick={handleCreateCategory} className="btn-submit">Speichern</button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Modal */}
      {showCouponModal && (
        <div className="modal-overlay" onClick={() => setShowCouponModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Neuen Gutscheincode erstellen</h2>
              <button onClick={() => setShowCouponModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Code * (nur Großbuchstaben & Zahlen)</label>
                <input
                  type="text"
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="z.B. ALEX10"
                  style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                />
              </div>
              
              <div className="form-group">
                <label>Affiliate-Partner *</label>
                <select
                  value={couponForm.affiliate_id}
                  onChange={(e) => setCouponForm({ ...couponForm, affiliate_id: e.target.value })}
                >
                  <option value="">-- Affiliate wählen --</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Rabatt (%)</label>
                <input
                  type="number"
                  value={couponForm.discount_percentage}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_percentage: parseFloat(e.target.value) })}
                  min="0"
                  max="100"
                  step="5"
                />
              </div>
              
              <div className="form-group">
                <label>Nutzungslimit</label>
                <select
                  value={couponForm.usage_limit_type}
                  onChange={(e) => setCouponForm({ ...couponForm, usage_limit_type: e.target.value })}
                >
                  <option value="unlimited">Unbegrenzt</option>
                  <option value="count">Anzahl Nutzungen</option>
                  <option value="date">Gültig bis Datum</option>
                </select>
              </div>
              
              {couponForm.usage_limit_type === 'count' && (
                <div className="form-group">
                  <label>Max. Nutzungen</label>
                  <input
                    type="number"
                    value={couponForm.max_uses || ''}
                    onChange={(e) => setCouponForm({ ...couponForm, max_uses: parseInt(e.target.value) })}
                    min="1"
                    placeholder="z.B. 100"
                  />
                </div>
              )}
              
              {couponForm.usage_limit_type === 'date' && (
                <div className="form-group">
                  <label>Gültig bis</label>
                  <input
                    type="date"
                    value={couponForm.valid_until || ''}
                    onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCouponModal(false)} className="btn-cancel">Abbrechen</button>
              <button onClick={handleCreateCoupon} className="btn-submit">Code erstellen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAffiliatePanel;
